import Icon from '@material-ui/core/Icon';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import type { Theme } from '@material-ui/core/styles';
import ModalActions from 'components/ModalActions';
import MyTable, { MyTableBody, MyTableCol, MyTableHeader, MyTableRow, MyTableWrapper } from 'components/MyTable';
import Spin from 'components/Spin';
import { WithStyles } from 'decorators/withStyles';
import type { SyntheticEvent } from 'react';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { deleteUser, listUsers } from 'store/Orbita/AccessPolicy/actions';

import type { IMyTableHeaderIndex } from 'components/MyTable/MyTableHeader';
import type { AppStyle } from 'decorators/withStyles';
import type { IAccessPolicyUserList } from 'store/Orbita/AccessPolicy/interfaces';

interface IProps {
  onEdit: (user: IAccessPolicyUserList[0]) => void;
}

interface IPropsFromConnect {
  isFetching: boolean;
  data: IAccessPolicyUserList;
  error?: any;

  listUsers?: typeof listUsers;
  deleteUser?: typeof deleteUser;
  classes?: any;
}

interface IStates {
  isDeleting: boolean;
  currentUser?: IAccessPolicyUserList[0];
  menuOptionsActive: any;
  showDeleteConfirmation: boolean;
}

const style: AppStyle = (theme: Theme) => ({
  tdAction: {
    textAlign: 'right',
    width: 50,
    paddingRight: '12px !important',
    cursor: 'pointer',
  },
  tabContainer: {
    background: 'white',
    borderRadius: 4,
    boxShadow: theme.shadows[1],
    padding: '12px 24px',
    overflow: 'hidden',
    width: '100%',
  },
  emptyMessage: {
    textAlign: 'center',
    paddingTop: 20,
  },
});

@WithStyles(style, { withTheme: true })
class AccessUsers extends React.Component<IProps & IPropsFromConnect, IStates> {
  headers: IMyTableHeaderIndex[] = [
    { title: 'Usuário', ordering: false },
    { title: 'E-mail', ordering: false },
    { title: 'Grupo', ordering: false },
    { title: '', ordering: false },
  ];

  constructor(props: IProps & IPropsFromConnect) {
    super(props);
    this.state = { menuOptionsActive: null, showDeleteConfirmation: false, isDeleting: false };
  }

  componentDidMount() {
    this.props.listUsers();
  }

  handleOpenMenu = (currentUser: IAccessPolicyUserList[0]) => (ev: SyntheticEvent) => {
    this.setState({ menuOptionsActive: ev.target, currentUser });
  };

  handleCloseMenuAction = () => {
    this.setState({ menuOptionsActive: null, currentUser: null });
  };

  handleEdit = () => {
    this.props.onEdit(this.state.currentUser);
    this.handleCloseMenuAction();
  };

  handleAskDelete = () => {
    this.setState({ showDeleteConfirmation: true, menuOptionsActive: null });
  };

  handleCancelDelete = () => {
    this.setState({ showDeleteConfirmation: false, currentUser: null });
  };

  handleDelete = async () => {
    const { currentUser } = this.state;
    this.setState({ showDeleteConfirmation: false, isDeleting: true, currentUser: null });

    try {
      await this.props.deleteUser(currentUser.id);
      toast.success('Sucesso, usuário removido');

      this.props.listUsers();
      this.setState({ isDeleting: false });
    } catch (err) {
      this.setState({ isDeleting: false });
      toast.error('Ops, erro na remoção do usuário');
    }
  };

  render() {
    const { isFetching, data, classes, error } = this.props;
    const { menuOptionsActive, showDeleteConfirmation, currentUser, isDeleting } = this.state;

    return (
      <Fragment>
        {(isFetching || isDeleting) && (
          <div className={classes.tabContainer}>
            <Spin />
          </div>
        )}

        {!!error && (
          <div className={classes.tabContainer}>
            <p>Um erro aconteceu...</p>
          </div>
        )}

        {!isFetching && !isDeleting && data && (
          <MyTableWrapper minWidth={this.headers.length * 100}>
            <div className={classes.tabContainer}>
              <MyTable>
                <MyTableHeader index={this.headers} />

                <MyTableBody>
                  {(!data || !data.length) && (
                    <tr>
                      <td colSpan={this.headers.length} className={classes.emptyMessage}>
                        Nenhum registro encontrado.
                      </td>
                    </tr>
                  )}
                  {Array.isArray(data) &&
                    data.map((data) => (
                      <MyTableRow key={data.id}>
                        <MyTableCol>{data.name}</MyTableCol>
                        <MyTableCol>{data.email}</MyTableCol>
                        <MyTableCol>{data.group_name}</MyTableCol>
                        <MyTableCol onClick={this.handleOpenMenu(data)} className={classes.tdAction}>
                          <Icon>more_horiz</Icon>
                        </MyTableCol>
                      </MyTableRow>
                    ))}
                </MyTableBody>
              </MyTable>
            </div>
          </MyTableWrapper>
        )}

        <Menu anchorEl={menuOptionsActive} open={!!menuOptionsActive} onClose={this.handleCloseMenuAction}>
          <MenuItem onClick={this.handleEdit}>Editar</MenuItem>
          <MenuItem onClick={this.handleAskDelete}>Remover</MenuItem>
        </Menu>

        <ModalActions open={showDeleteConfirmation} onClose={this.handleCancelDelete} onOk={this.handleDelete} title='Atenção' buttonOk='Continuar'>
          {currentUser && <p>Deseja realmente remover o usuário {currentUser.name}?</p>}
        </ModalActions>
      </Fragment>
    );
  }
}

function mapStateToProps(state: any) {
  return {
    isFetching: state.orbita.AccessPolicy.users.isFetching,
    error: state.orbita.AccessPolicy.users.error,
    data: state.orbita.AccessPolicy.users || [],
  };
}

export default connect<IPropsFromConnect, {}, IProps>(mapStateToProps, {
  listUsers,
  deleteUser,
})(AccessUsers);
